import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleNavigation from '../../components/ui/RoleNavigation';
import NotificationCenter from '../../components/ui/NotificationCenter';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProgressIndicator from './components/ProgressIndicator';
import SocialHandleInput from './components/SocialHandleInput';
import FollowerRangeSelect from './components/FollowerRangeSelect';
import CategoryChip from './components/CategoryChip';
import ScorePreview from './components/ScorePreview';
import PostingFrequencySelect from './components/PostingFrequencySelect';
import AudienceDemographicsSelect from './components/AudienceDemographicsSelect';
import TooltipInfo from './components/TooltipInfo';

const UserOnboardingScreen = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [calculatedScore, setCalculatedScore] = useState(0);

  const steps = [
    { id: 1, label: 'Social Accounts' },
    { id: 2, label: 'Audience Size' },
    { id: 3, label: 'Content Focus' },
    { id: 4, label: 'Additional Info' }
  ];

  const [formData, setFormData] = useState({
    socialHandles: {
      instagram: '',
      tiktok: '',
      youtube: '',
      twitter: ''
    },
    followerRanges: {
      instagram: '',
      tiktok: '',
      youtube: '',
      twitter: ''
    },
    categories: [],
    postingFrequency: '',
    audienceDemographics: ''
  });

  const [errors, setErrors] = useState({});

  const contentCategories = [
    { value: 'lifestyle', label: 'Lifestyle' },
    { value: 'fashion', label: 'Fashion' },
    { value: 'tech', label: 'Tech' },
    { value: 'food', label: 'Food' },
    { value: 'travel', label: 'Travel' },
    { value: 'fitness', label: 'Fitness' },
    { value: 'beauty', label: 'Beauty' }
  ];

  useEffect(() => {
    calculateScore();
  }, [formData]);

  const calculateScore = () => {
    let score = 0;

    const handleCount = Object.values(formData?.socialHandles)?.filter(h => h?.trim())?.length;
    score += handleCount * 50;

    const followerRangeScores = {
      '1k-10k': 50,
      '10k-50k': 100,
      '50k-100k': 150,
      '100k-500k': 200,
      '500k-1m': 250,
      '1m+': 300
    };

    Object.values(formData?.followerRanges)?.forEach(range => {
      if (range) score += followerRangeScores?.[range] || 0;
    });

    score += formData?.categories?.length * 30;

    const frequencyScores = {
      'daily': 100,
      '4-6-weekly': 80,
      '2-3-weekly': 60,
      'weekly': 40,
      'bi-weekly': 20,
      'monthly': 10
    };
    score += frequencyScores?.[formData?.postingFrequency] || 0;

    if (formData?.audienceDemographics) score += 50;

    setCalculatedScore(Math.min(score, 1000));
  };

  const handleSocialHandleChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialHandles: {
        ...prev?.socialHandles,
        [platform]: value
      }
    }));
    if (errors?.[`socialHandles.${platform}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[`socialHandles.${platform}`];
        return newErrors;
      });
    }
  };

  const handleFollowerRangeChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      followerRanges: {
        ...prev?.followerRanges,
        [platform]: value
      }
    }));
    if (errors?.[`followerRanges.${platform}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[`followerRanges.${platform}`];
        return newErrors;
      });
    }
  };

  const handleCategoryToggle = (categoryValue) => {
    setFormData(prev => ({
      ...prev,
      categories: prev?.categories?.includes(categoryValue)
        ? prev?.categories?.filter(c => c !== categoryValue)
        : [...prev?.categories, categoryValue]
    }));
    if (errors?.categories) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.categories;
        return newErrors;
      });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      const hasAtLeastOneHandle = Object.values(formData?.socialHandles)?.some(h => h?.trim());
      if (!hasAtLeastOneHandle) {
        newErrors.socialHandles = 'Please provide at least one social media handle';
      }
    }

    if (step === 1) {
      const filledHandles = Object.entries(formData?.socialHandles)?.filter(([_, value]) => value?.trim())?.map(([platform]) => platform);

      filledHandles?.forEach(platform => {
        if (!formData?.followerRanges?.[platform]) {
          newErrors[`followerRanges.${platform}`] = 'Please select follower range';
        }
      });
    }

    if (step === 2) {
      if (formData?.categories?.length === 0) {
        newErrors.categories = 'Please select at least one content category';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps?.length - 1) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    navigate('/user-dashboard');
  };

  const handleSubmit = () => {
    if (validateStep(currentStep)) {
      navigate('/user-dashboard');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[var(--color-foreground)]">
                Connect Your Social Accounts
              </h2>
              <TooltipInfo content="Connecting your social accounts helps us verify your reach and calculate your SocialCents Score. We never post without your permission." />
            </div>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mb-6">
              Add your social media handles to get started. You can add more platforms later.
            </p>
            {errors?.socialHandles && (
              <div className="bg-[var(--color-error)]/10 border border-[var(--color-error)] rounded-lg p-4 mb-6">
                <p className="text-sm text-[var(--color-error)]">{errors?.socialHandles}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <SocialHandleInput
                platform="instagram"
                value={formData?.socialHandles?.instagram}
                onChange={handleSocialHandleChange}
                error={errors?.['socialHandles.instagram']}
              />
              <SocialHandleInput
                platform="tiktok"
                value={formData?.socialHandles?.tiktok}
                onChange={handleSocialHandleChange}
                error={errors?.['socialHandles.tiktok']}
              />
              <SocialHandleInput
                platform="youtube"
                value={formData?.socialHandles?.youtube}
                onChange={handleSocialHandleChange}
                error={errors?.['socialHandles.youtube']}
              />
              <SocialHandleInput
                platform="twitter"
                value={formData?.socialHandles?.twitter}
                onChange={handleSocialHandleChange}
                error={errors?.['socialHandles.twitter']}
              />
            </div>
          </div>
        );

      case 1:
        const filledHandles = Object.entries(formData?.socialHandles)?.filter(([_, value]) => value?.trim())?.map(([platform]) => platform);

        return (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[var(--color-foreground)]">
                Tell Us About Your Audience
              </h2>
              <TooltipInfo content="Your follower count helps us match you with relevant brand campaigns and calculate your earning potential. We use ranges to make this easier." />
            </div>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mb-6">
              Select the approximate follower count for each connected platform.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {filledHandles?.map(platform => (
                <FollowerRangeSelect
                  key={platform}
                  platform={platform}
                  value={formData?.followerRanges?.[platform]}
                  onChange={handleFollowerRangeChange}
                  error={errors?.[`followerRanges.${platform}`]}
                />
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[var(--color-foreground)]">
                Choose Your Content Categories
              </h2>
              <TooltipInfo content="Selecting your content focus helps us recommend campaigns that align with your niche and audience interests. You can select multiple categories." />
            </div>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mb-6">
              Select all categories that match your content style. This helps us recommend relevant brand campaigns.
            </p>
            {errors?.categories && (
              <div className="bg-[var(--color-error)]/10 border border-[var(--color-error)] rounded-lg p-4 mb-6">
                <p className="text-sm text-[var(--color-error)]">{errors?.categories}</p>
              </div>
            )}
            <div className="flex flex-wrap gap-3 md:gap-4">
              {contentCategories?.map(category => (
                <CategoryChip
                  key={category?.value}
                  category={category}
                  selected={formData?.categories?.includes(category?.value)}
                  onClick={handleCategoryToggle}
                />
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[var(--color-foreground)]">
                Final Details
              </h2>
              <TooltipInfo content="These details help us understand your content strategy and audience demographics for better campaign matching." />
            </div>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mb-6">
              Help us understand your content strategy and audience better.
            </p>
            <div className="space-y-6">
              <PostingFrequencySelect
                value={formData?.postingFrequency}
                onChange={(value) => setFormData(prev => ({ ...prev, postingFrequency: value }))}
                error={errors?.postingFrequency}
              />
              <AudienceDemographicsSelect
                value={formData?.audienceDemographics}
                onChange={(value) => setFormData(prev => ({ ...prev, audienceDemographics: value }))}
                error={errors?.audienceDemographics}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--color-background)] via-[#1a2332] to-[var(--color-background)]">
      <RoleNavigation role="influencer" userName="New User" />
      <div className="fixed top-4 right-4 z-[1100] lg:top-4 lg:right-6">
        <NotificationCenter role="influencer" />
      </div>
      <div className="pt-[80px] md:pt-[100px] lg:pt-[120px] pb-8 md:pb-12 lg:pb-16 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10 lg:mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[var(--color-primary)]/20 to-[var(--color-secondary)]/20 px-4 py-2 rounded-full mb-4">
              <Icon name="Sparkles" size={20} color="var(--color-primary)" />
              <span className="text-sm md:text-base font-medium text-[var(--color-primary)]">
                Welcome to SocialCents
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--color-foreground)] mb-3">
              Let's Build Your Profile
            </h1>
            <p className="text-base md:text-lg text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
              Complete your profile to unlock earning opportunities and get matched with brands that align with your content.
            </p>
          </div>

          <ProgressIndicator
            currentStep={currentStep}
            totalSteps={steps?.length}
            steps={steps}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
            <div className="lg:col-span-2">
              <div className="bg-[var(--color-card)] rounded-2xl p-6 md:p-8 lg:p-10 border border-[var(--color-border)] shadow-xl">
                {renderStepContent()}

                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 md:mt-10 lg:mt-12 pt-6 border-t border-[var(--color-border)]">
                  <Button
                    variant="ghost"
                    onClick={handleSkip}
                    iconName="SkipForward"
                    iconPosition="right"
                    className="w-full sm:w-auto order-2 sm:order-1"
                  >
                    Skip for Now
                  </Button>

                  <div className="flex items-center gap-3 w-full sm:w-auto order-1 sm:order-2">
                    {currentStep > 0 && (
                      <Button
                        variant="outline"
                        onClick={handleBack}
                        iconName="ArrowLeft"
                        iconPosition="left"
                        className="flex-1 sm:flex-none"
                      >
                        Back
                      </Button>
                    )}
                    {currentStep < steps?.length - 1 ? (
                      <Button
                        variant="default"
                        onClick={handleNext}
                        iconName="ArrowRight"
                        iconPosition="right"
                        className="flex-1 sm:flex-none"
                      >
                        Continue
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        onClick={handleSubmit}
                        iconName="Check"
                        iconPosition="right"
                        className="flex-1 sm:flex-none"
                      >
                        Complete Setup
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-[100px]">
                <ScorePreview score={calculatedScore} maxScore={1000} />
                
                <div className="mt-6 bg-gradient-to-br from-[var(--color-primary)]/10 to-[var(--color-secondary)]/10 rounded-xl p-6 border border-[var(--color-primary)]/20">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center flex-shrink-0">
                      <Icon name="Lightbulb" size={20} color="#FFFFFF" />
                    </div>
                    <div>
                      <h4 className="text-base md:text-lg font-semibold text-[var(--color-foreground)] mb-2">
                        Pro Tip
                      </h4>
                      <p className="text-sm text-[var(--color-muted-foreground)]">
                        Higher scores unlock premium campaigns with better cashback rates. Complete all fields to maximize your earning potential!
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserOnboardingScreen;